
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { getProductBySlug, getProducts, Product as ProductType } from '../api/products';
import { useCart } from '../context/CartContext';
import { getImageUrl, fixHtmlContent } from '../utils/image';
import { formatPrice } from '../utils/formatPrice';
import ProductCard from '../components/ProductCard';
import TestimonialsCarousel from '../components/TestimonialsCarousel';
import QuickOrderModal from '../components/QuickOrderModal';
import ProductCountdown from '../components/ProductCountdown';
import InlineOrderForm from '../components/InlineOrderForm';

const ProductPage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const { t, i18n } = useTranslation();
    const { addToCart } = useCart();
    const [product, setProduct] = useState<ProductType | null>(null);
    const [relatedProducts, setRelatedProducts] = useState<ProductType[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<ProductType | null>(null);
    const [selectedVariant, setSelectedVariant] = useState<ProductType['variants'][0] | null>(null);
    const [quantity, setQuantity] = useState(1);

    useEffect(() => {
        const fetchProduct = async () => {
            if (!slug) return;
            try {
                const data = await getProductBySlug(slug);
                setProduct(data);
                if (data && data.images.length > 0) {
                    setCurrentImageIndex(0);
                }
                // Set default variant if available
                if (data && data.variants && data.variants.length > 0) {
                    // Check if there's a variant explicitly named "standart"
                    const standartVariant = data.variants.find(v => v.name.toLowerCase() === 'standart');
                    setSelectedVariant(standartVariant || null);
                } else {
                    setSelectedVariant(null);
                }

                // Fetch related products from same category
                if (data) {
                    const allProducts = await getProducts();
                    const currentCategorySlugs = data.categories?.map(c => c.slug) || [data.category?.slug].filter(Boolean);

                    const related = allProducts
                        .filter(p =>
                            p.id !== data.id &&
                            // Check if share any category
                            (
                                p.categories?.some(c => currentCategorySlugs.includes(c.slug)) ||
                                (p.category?.slug && currentCategorySlugs.includes(p.category.slug))
                            )
                        )
                        .slice(0, 5);
                    setRelatedProducts(related);
                }
            } catch (error) {
                console.error('Failed to fetch product', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProduct();
    }, [slug]);

    if (loading) return <div className="p-8 text-center">{t('common.loading')}</div>;
    if (!product) return <div className="p-8 text-center">Product not found</div>;

    const isRTL = i18n.dir() === 'rtl';
    const title = isRTL ? product.title_ar : product.title_fr;
    const shortDescription = fixHtmlContent(isRTL ? product.short_desc_ar : product.short_desc_fr);
    const fullDescription = fixHtmlContent(isRTL ? product.description_ar : product.description_fr);
    const description = fullDescription || shortDescription;

    // Use variant price if selected, otherwise product price
    const displayPrice = selectedVariant
        ? parseFloat(selectedVariant.price)
        : (product.discount_price || product.price);

    const parseBadges = (badgesData: string[] | string | undefined): string[] => {
        if (!badgesData) return [];
        if (Array.isArray(badgesData)) return badgesData;
        if (typeof badgesData === 'string' && badgesData.trim()) {
            try {
                return JSON.parse(badgesData);
            } catch {
                return badgesData.split(',').map(b => b.trim()).filter(Boolean);
            }
        }
        return [];
    };

    const badgesFr = parseBadges(product.badges);
    const badgesAr = parseBadges(product.badges_ar);

    // Logic: If RTL and Arabic badges exist, show them. Otherwise fallback to French badges.
    const badges = (isRTL && badgesAr.length > 0) ? badgesAr : badgesFr;

    const parseImages = (imagesData: string[] | string | undefined): string[] => {
        if (!imagesData) return [];
        if (Array.isArray(imagesData)) return imagesData;
        if (typeof imagesData === 'string' && imagesData.trim()) {
            try {
                const parsed = JSON.parse(imagesData);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return imagesData.split(',').map(i => i.trim()).filter(Boolean);
            }
        }
        return [];
    };

    const images = parseImages(product.images);

    const nextImage = () => {
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
    };

    const prevImage = () => {
        setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const stock = product.stock || 0;
    const isLowStock = stock > 0 && stock < 10;
    const isOutOfStock = stock === 0;

    const openQuickOrder = (prod: ProductType) => {
        setSelectedProduct(prod);
        setIsModalOpen(true);
    };

    return (
        <div className="bg-gradient-to-br from-white to-green-50 min-h-screen">
            {/* ... (Keep existing layout) ... */}
            <div className="container mx-auto px-4 py-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    {/* Gallery Code Omitted for brevity, kept same */}
                    <motion.div
                        className="space-y-6"
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="bg-white rounded-3xl overflow-hidden shadow-card sticky top-24 relative group">
                            <img
                                src={getImageUrl(images[currentImageIndex])}
                                alt={title}
                                className="w-full h-auto transition-transform duration-500 group-hover:scale-105"
                            />
                            {/* Navigation Arrows */}
                            {images.length > 1 && (
                                <>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            prevImage();
                                        }}
                                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-dark p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 transform hover:scale-110 z-10"
                                        aria-label="Previous image"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-6 h-6">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                                        </svg>
                                    </button>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            nextImage();
                                        }}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-dark p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 transform hover:scale-110 z-10"
                                        aria-label="Next image"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-6 h-6">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                                        </svg>
                                    </button>
                                    <div className="absolute bottom-4 right-4 bg-black/50 text-white text-xs font-bold px-3 py-1 rounded-full backdrop-blur-sm">
                                        {currentImageIndex + 1} / {images.length}
                                    </div>
                                </>
                            )}
                        </div>
                    </motion.div>

                    {/* Details */}
                    <motion.div
                        className="flex flex-col"
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                    >
                        {/* Category & Badges */}
                        <div className="flex flex-wrap items-center gap-2 mb-4">
                            {/* <span className="text-sm text-neutral-500 uppercase tracking-wider">
                                {product.categories?.[0]?.name_fr || product.category?.name_fr}
                            </span> */}
                            {badges && badges.length > 0 && badges.map((badge, idx) => (
                                <span key={idx} className="bg-primary/10 text-primary text-xs font-bold px-3 py-1 rounded-full">
                                    {badge}
                                </span>
                            ))}
                        </div>

                        {/* Title */}
                        <h1 className="text-4xl md:text-5xl font-bold text-dark mb-6 leading-tight">{title}</h1>

                        {/* Price */}
                        <div className="flex flex-col sm:flex-row sm:items-end gap-2 sm:gap-4 mb-6">
                            {(product.discount_price && !selectedVariant) ? (
                                <>
                                    <span className="text-4xl font-bold text-primary">
                                        {formatPrice(product.discount_price, product.currency, isRTL ? 'ar-MA' : 'fr-MA')}
                                    </span>
                                    <div className="flex items-center gap-3">
                                        <span className="text-2xl text-neutral-400 line-through">
                                            {formatPrice(product.price, product.currency, isRTL ? 'ar-MA' : 'fr-MA')}
                                        </span>
                                        <span className="bg-accent text-white text-sm font-bold px-3 py-1 rounded-full">
                                            -{Math.round(((product.price - product.discount_price) / product.price) * 100)}%
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <span className="text-4xl font-bold text-primary">
                                    {formatPrice(displayPrice, product.currency, isRTL ? 'ar-MA' : 'fr-MA')}
                                </span>
                            )}
                        </div>

                        {/* Variants Selection */}
                        {product.variants && product.variants.length > 0 && (
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2">{t('product.selectOption', 'Select Option')}</label>
                                <div className="flex flex-wrap gap-2">
                                    {/* Standard / Default Option */}
                                    <button
                                        onClick={() => setSelectedVariant(null)}
                                        className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all
                                            ${selectedVariant === null
                                                ? 'border-primary bg-primary text-white'
                                                : 'border-gray-200 bg-white text-gray-700 hover:border-primary/50'
                                            }`}
                                    >
                                        {/* {t('product.standard', 'Standard')} */}
                                        {isRTL ? (product.unit_name_ar || product.unit_name_fr) : product.unit_name_fr}
                                    </button>

                                    {product.variants.map((variant) => (
                                        <button
                                            key={variant.id}
                                            onClick={() => setSelectedVariant(variant)}
                                            className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all
                                                ${selectedVariant?.id === variant.id
                                                    ? 'border-primary bg-primary text-white'
                                                    : 'border-gray-200 bg-white text-gray-700 hover:border-primary/50'
                                                }`}
                                        >
                                            {isRTL ? (variant.name_ar || variant.name) : variant.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Quantity Selector */}
                        {!isOutOfStock && (
                            <div className="mb-8">
                                <label className="block text-sm font-medium text-gray-700 mb-3">{t('product.quantity', 'Quantity')}</label>
                                <div className="flex items-center gap-4">
                                    <div className="flex items-center border-2 border-neutral-200 rounded-xl bg-white overflow-hidden shadow-sm">
                                        <button
                                            onClick={() => setQuantity(q => Math.max(1, q - 1))}
                                            className="w-12 h-12 flex items-center justify-center text-xl font-bold text-gray-600 hover:bg-neutral-50 transition-colors"
                                        >
                                            -
                                        </button>
                                        <div className="w-12 h-12 flex items-center justify-center font-bold text-lg border-x border-neutral-100">
                                            {quantity}
                                        </div>
                                        <button
                                            onClick={() => setQuantity(q => q + 1)}
                                            className="w-12 h-12 flex items-center justify-center text-xl font-bold text-gray-600 hover:bg-neutral-50 transition-colors"
                                        >
                                            +
                                        </button>
                                    </div>
                                    <div className="text-sm text-gray-500 font-medium">
                                        {t('product.totalPrice', 'Total')}:
                                        <span className="text-primary font-bold ml-1">
                                            {formatPrice(displayPrice * quantity, product.currency, isRTL ? 'ar-MA' : 'fr-MA')}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Stock Counter */}
                        <div className="mt-8 mb-6">
                            {isOutOfStock ? (
                                <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-4 flex items-center gap-3">
                                    <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                    <span className="font-bold text-red-700">{t('product.outOfStock')}</span>
                                </div>
                            ) : isLowStock ? (
                                <div className="bg-orange-50 border-2 border-orange-200 rounded-2xl p-4 flex items-center gap-3">
                                    <svg className="w-6 h-6 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                    <div>
                                        <span className="font-bold text-orange-700">{t('common.lowStock')}</span>
                                        <span className="text-orange-600 ml-2">({stock} {t('product.remaining')})</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-4 flex items-center gap-3">
                                    <svg className="w-6 h-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span className="font-bold text-green-700">{t('product.stock')}</span>
                                </div>
                            )}
                        </div>

                        {/* Trust Badges */}
                        <div className="grid grid-cols-2 gap-3 mb-8">
                            {/* ... Badges content ... */}
                            <div className="bg-white rounded-2xl p-4 shadow-soft flex items-center gap-3">
                                <svg className="w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                                <div>
                                    <div className="font-bold text-dark text-sm">{t('product.cod')}</div>
                                    <div className="text-xs text-gray-600">{t('product.codDesc')}</div>
                                </div>
                            </div>
                            <div className="bg-white rounded-2xl p-4 shadow-soft flex items-center gap-3">
                                <svg className="w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                                <div>
                                    <div className="font-bold text-dark text-sm">{t('product.fastDelivery')}</div>
                                    <div className="text-xs text-gray-600">{t('product.fastDeliveryDesc')}</div>
                                </div>
                            </div>
                        </div>

                        {/* Inline Order Form and Add to Cart */}
                        {!isOutOfStock && (
                            <div className="mb-8 flex flex-col gap-4">
                                <InlineOrderForm product={product} variant={selectedVariant} quantity={quantity} />

                                <button
                                    onClick={() => addToCart(product, quantity, selectedVariant)}
                                    disabled={isOutOfStock}
                                    className="w-full py-4 px-6 bg-gradient-primary text-white font-bold rounded-2xl hover:shadow-glow transition-smooth text-lg flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                    {t('common.addToCart')}
                                </button>
                            </div>
                        )}

                        {/* Short Description */}
                        <div className="prose prose-neutral max-w-none mb-6">
                            <div
                                className="text-lg leading-relaxed text-neutral-700"
                                dangerouslySetInnerHTML={{ __html: shortDescription || '' }}
                            />
                        </div>

                        {/* Full Description Section */}
                        {fullDescription && fullDescription !== shortDescription && (
                            <div className="bg-white rounded-3xl p-6 shadow-soft mb-8">
                                <h3 className="font-bold text-dark text-2xl mb-4 flex items-center gap-2">
                                    <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    {t('product.fullDescription')}
                                </h3>
                                <div className="prose prose-lg prose-neutral max-w-none">
                                    <div
                                        className="text-base leading-relaxed text-neutral-700 whitespace-pre-line"
                                        dangerouslySetInnerHTML={{ __html: fullDescription || '' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Description Section (fallback) */}
                        {!fullDescription && description && (
                            <div className="bg-white rounded-3xl p-6 shadow-soft mb-8">
                                <h3 className="font-bold text-dark text-2xl mb-4 flex items-center gap-2">
                                    <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    {t('product.description')}
                                </h3>
                                <div className="prose prose-lg prose-neutral max-w-none">
                                    <div
                                        className="text-base leading-relaxed text-neutral-700 whitespace-pre-line"
                                        dangerouslySetInnerHTML={{ __html: description || '' }}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Ingredients Section */}
                        {(() => {
                            const parseIngredients = (data: string[] | string | undefined): string[] => {
                                if (!data) return [];
                                if (Array.isArray(data)) return data;
                                if (typeof data === 'string' && data.trim()) {
                                    try {
                                        const parsed = JSON.parse(data);
                                        return Array.isArray(parsed) ? parsed : [];
                                    } catch {
                                        return data.split(',').map(i => i.trim()).filter(Boolean);
                                    }
                                }
                                return [];
                            };

                            const ingredientsFr = parseIngredients(product.ingredients_fr);
                            const ingredientsAr = parseIngredients(product.ingredients_ar);
                            const ingredients = (isRTL && ingredientsAr.length > 0) ? ingredientsAr : ingredientsFr;

                            if (ingredients && ingredients.length > 0) {
                                return (
                                    <div className="bg-white rounded-3xl p-6 shadow-soft mb-8">
                                        <h3 className="font-bold text-dark text-2xl mb-4 flex items-center gap-2">
                                            <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                                            </svg>
                                            {t('product.ingredients', 'Ingredients')}
                                        </h3>
                                        <div className="flex flex-wrap gap-2">
                                            {ingredients.map((ingredient, idx) => (
                                                <span key={idx} className="bg-neutral-100 text-neutral-700 px-4 py-2 rounded-xl text-sm font-medium border border-neutral-200">
                                                    {ingredient}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                );
                            }
                            return null;
                        })()}



                        {/* Floating Motivating Buy Button */}
                        {!isOutOfStock && (
                            <motion.div
                                initial={{ y: 100, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ delay: 0.5, type: 'spring', stiffness: 200, damping: 20 }}
                                className="fixed bottom-6 left-0 right-0 z-50 flex justify-center pointer-events-none px-4"
                            >
                                <button
                                    onClick={() => {
                                        const formEl = document.getElementById('inline-order-form');
                                        if (formEl) {
                                            const yOffset = -80; // offset for fixed header
                                            const y = formEl.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                            window.scrollTo({ top: y, behavior: 'smooth' });

                                            // Optional: Focus the first input field slightly after scrolling
                                            setTimeout(() => {
                                                document.getElementById('name')?.focus();
                                            }, 800);
                                        }
                                    }}
                                    className="w-full md:w-auto pointer-events-auto py-4 px-8 bg-gradient-to-r from-primary to-green-600 text-white font-black rounded-full shadow-glow-lg hover:scale-105 hover:shadow-glow-xl transition-all flex items-center justify-center gap-3 animate-pulse border-2 border-white/50"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                    </svg>
                                    <span className="text-lg tracking-wide uppercase">{t('quickOrderForm.buyNowMotivate')}</span>
                                </button>
                            </motion.div>
                        )}
                    </motion.div>
                </div>
            </div>

            {/* Related Products Section */}
            {relatedProducts.length > 0 && (
                <div className="bg-white py-16">
                    <div className="container mx-auto px-4">
                        <h2 className="text-3xl md:text-4xl font-bold text-dark mb-8 text-center">
                            {t('product.relatedProducts')}
                        </h2>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
                            {relatedProducts.map(prod => (
                                <ProductCard key={prod.id} product={prod} onQuickOrder={openQuickOrder} />
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Testimonials Section */}
            <div className="py-16 bg-gradient-to-b from-white to-light">
                <div className="container mx-auto px-4">
                    <TestimonialsCarousel productId={product?.id} />
                </div>
            </div>

            {/* Quick Order Modal */}
            <QuickOrderModal
                isOpen={isModalOpen}
                closeModal={() => setIsModalOpen(false)}
                product={selectedProduct}
                variant={selectedVariant}
                initialQuantity={quantity}
            />
        </div>
    );
};

export default ProductPage;
